import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiClient } from '@discovr/common';
import type { DataResponse } from '@discovr/types';
import { recreateFailure, shopifyRecreateCopy } from '../../copy';
import type { EditCouponConfigInput, ShopifyCampaignCoupon } from './useCampaignShopify';
import { refreshCampaignShopify } from './shopifyCouponsRefresh';

/** Response of POST /campaigns/{id}/shopify/coupons/{coupon_id}/recreate.
 *  `coupon` is omitted when the recreate succeeded but the row could not be
 *  read back. */
export interface RecreateCouponResult {
  status: 'recreated' | 'relinked';
  coupon?: ShopifyCampaignCoupon;
}

export interface RecreateCouponVariables {
  couponId: string;
  /** The row's code, for the toasts only; never sent. */
  code: string;
  /** The edit form's terms. The server takes the code from the row. */
  input: EditCouponConfigInput;
}

/** Recreates a code Shopify renamed, under its old name, for a live row. */
export function useShopifyRecreateCoupon(campaignId: string) {
  const qc = useQueryClient();
  return useMutation({
    // An empty 200 body still means the recreate went through.
    mutationFn: async ({
      couponId,
      input,
    }: RecreateCouponVariables): Promise<RecreateCouponResult | undefined> => {
      const res = await apiClient.post<DataResponse<RecreateCouponResult>>(
        `/campaigns/${encodeURIComponent(campaignId)}/shopify/coupons/${encodeURIComponent(couponId)}/recreate`,
        input,
      );
      return res.data?.data;
    },
    onSuccess: (res) => {
      // Always reloads: the row leaves "Changed in Shopify", and a response
      // without `coupon` gives nothing to show in its place.
      refreshCampaignShopify(qc, campaignId);
      if (res?.status === 'relinked') {
        toast.warning(shopifyRecreateCopy.relinked, { duration: 15_000 });
        return;
      }
      toast.success(shopifyRecreateCopy.recreated);
    },
    onError: (err, { code }) => {
      const failure = recreateFailure(err, code);
      if (failure.refreshList) refreshCampaignShopify(qc, campaignId);
      toast.error(failure.message);
    },
  });
}

export type ShopifyRecreateCouponMutation = ReturnType<typeof useShopifyRecreateCoupon>;
